import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import { HstDataTransfer } from "csi/comm/Aspera";
import { deleteFileData, getAsperaFileData, getFileData } from "csi/service/fileService";
import { useStore } from "csi/state/hooks";
import { IFileData } from "csi/typings";
import xml from "csi/xml";
import ErrorDialog from "designSystem/components/dialog/ErrorDialog";
import MessageDialog from "designSystem/components/dialog/MessageDialog";
import FroalaEditorComponent, { disableEditorToolbar, enableEditorToolbar, getEditorContent } from "designSystem/components/editor/FroalaEditorComponent";
import { ButtonIcon, CSS_DISABLE, CSS_SHOW } from "designSystem/components/editor/config";
import { MAX_HEIGHT_OF_TOOLBAR, MIN_HEIGHT_OF_TOOLBAR, PLACEHOLDER_HEIGHT, PRE_SEND_BUTTON, SEND_BUTTON, openPopupsInRightPane } from "designSystem/components/editor/configForRightPane";
import File from "designSystem/components/file/File";
import FlexRowCenter from "designSystem/components/flex/FlexRowCenter";
import Text from "designSystem/components/text/Text";
import { useLocale } from "designSystem/locale";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { UserContext } from "designSystem/user";
import { RegisterCommand } from "froala-editor";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import React, { Fragment, useEffect, useRef, useState } from "react";
import FroalaEditor from "react-froala-wysiwyg";
import CommentCard from "./CommentCard";
import CommentOutdatedBanner from "./CommentOutdatedBanner";
import CommentStatusIcon from "./CommentStatusIcon";
import InvalidAttachmentDialog from "./InvalidAttachmentDialog";

const StyledLink = styled(Link)((
	{
		theme,
	},
) => ({
	display: "inline-flex",
	justifyContent: "flex-end",
}));

const StyledBox = styled(Box)((
	{
		theme,
	},
) => ({
	flexGrow: 1,
	overflow: "hidden auto",
}));

export const CommentDetail = observer(() => {
	const loc = useLocale();
	const ref = useRef<FroalaEditor>();
	const { commentsStore, rightPaneStore } = useStore();
	const {
		canReply,
		closeErrorDialog,
		fetchSelectedComment,
		getCommentSummary,
		isEditing,
		isReply,
		isSelectedCommentStale,
		mentionUserText,
		onContentChangeCallback,
		selectedComment,
		selectedCommentReplyUrls,
		selectedCommentUrl,
		setSelectedCommentResolved,
		setReplyPinButtonActive,
		startReply,
		stopReply,
		replyComment,
		userProvider,
	} = commentsStore;


	const { isResolved, statusDate, statusUser } = getCommentSummary(selectedCommentUrl);
	const canResolve = !isEditing && xml.nodeHasAccess(selectedComment, xml.RIGHT_ADMIN);

	const [addedFiles, setAddedFiles] = useState<IFileData[]>([]);
	const [deletedFiles, setDeletedFiles] = useState<IFileData[]>([]);
	const [asperaData, setAsperaData] = useState<HstDataTransfer[]>([]);

	// push the file to the modal dialog editor
	const pushFilesPreOpenDialog = () => addedFiles;

	// sync up with the files from the modal dialog editor
	const pullFiles = (addedFiles: IFileData[], deletedFiles: IFileData[], hstData?: HstDataTransfer[]) => {
		setAddedFiles(addedFiles);
		setDeletedFiles(deletedFiles);
		if (hstData?.length) {
			setAsperaData(hstData);
		}
	};

	// attach the files
	const onAddAttachment = (files: File[], hstData: HstDataTransfer) => {
		if (hstData) {
			setAsperaData([...asperaData, hstData]);
			setAddedFiles([...addedFiles, ...hstData.fileNames.map(getAsperaFileData)]);
		} else {
			setAddedFiles([...addedFiles, ...files.map(getFileData)]);
		}

		startReply();

		// Note: startReply will make the component re-render, let it finish first
		// when attaching file, the blur event will fire for sure which would disable the toolbar, enable this here
		setTimeout(() => enableEditorToolbar(ref.current), 0);
	};

	// pop up the dialog when error occurs
	const onAttachError = (errorFileNames: string[]) => {
		rightPaneStore.openDialog(InvalidAttachmentDialog, { filenames: errorFileNames });
	};

	// update the deleted ones
	const onDeleteAttachment = (fileData: IFileData) => {
		const remainingFiles = _.difference(addedFiles, [fileData]);
		if (remainingFiles.length != addedFiles.length) {
			setAddedFiles(remainingFiles);
		} else {
			setDeletedFiles([...deletedFiles, fileData]);
		}

		// take care of placeholder at the bottom
		if (remainingFiles.length == 0 && !getContent()) {
			stopReply();
		}
	};

	// get the content of the editor
	const getContent = (): string => getEditorContent(ref.current);

	RegisterCommand(PRE_SEND_BUTTON, {
		focus: false,
		icon: ButtonIcon.preSend,
		refresh: (btn) => {
			if (!getContent() && !addedFiles.length) {
				btn.addClass(CSS_SHOW + " " + CSS_DISABLE);
			} else {
				btn.removeClass(CSS_SHOW + " " + CSS_DISABLE);
			}
		},
		undo: false,
	});

	RegisterCommand(SEND_BUTTON, {
		callback: async () => {
			const reply = getContent();
			const text = reply.trim();
			rightPaneStore.openDialog(MessageDialog, {
				category: "progress",
				messageRes: "SavingInProgress",
				titleRes: "CommentReplySave",
			});

			if (text || addedFiles.length) {
				try {
					// reply attachments
					await replyComment(selectedCommentUrl, text, addedFiles, asperaData);

					// after reply, reset the files and content so that user can be able to start over with a brand new round
					userProvider.clearSearchCache();
					addedFiles.forEach(deleteFileData);
					setAddedFiles([]);
					rightPaneStore.closeDialog();
				} catch (error) {
					// error while publishing files
					rightPaneStore.openDialog(ErrorDialog, {
						messageRes: "RequestErrorMessageAndPotentialReasons",
						onClose: closeErrorDialog,
						titleRes: "CommentReplySave",
					});
					console.error(error.message);
				}
			}
		},
		focus: false,
		icon: ButtonIcon.send,
		refresh: (btn) => {
			if (!getContent() && !addedFiles.length) {
				btn.removeClass(CSS_SHOW);
			} else {
				btn.addClass(CSS_SHOW);
			}
		},
		undo: false,
	});

	// when mouse over and out reply comments add pin button set the state to handle blur event on text editor
	const toggleHover = (e) => {
		setReplyPinButtonActive(e.type == "mouseenter");
	};

	// when blur, disable the toolbar
	const onBlur = (object, e) => {
		disableEditorToolbar(ref.current);

		// we can't get the latest value of addedFiles due to the context, have to save the value on instance instead
		const instance = ref.current["attachFiles"];

		//if mouse over addpin section buttons dont exit out of add reply edit mode
		if (!commentsStore.replyPinButtonActive && !getContent() && (!instance || instance.length == 0)) {
			stopReply();
		}
	};

	// deal with the popups
	const onOpenPopups = (a) => openPopupsInRightPane(ref.current, a);

	useEffect(() => {
		if (ref.current) {
			// track the attachments
			ref.current["attachFiles"] = addedFiles;
		}
	}, [isReply, addedFiles]);

	return (
		<>
			<FlexRowCenter
				borderBottom={STANDARD_BORDER_STYLE}
				flexShrink={0}
				height={40}
				px={3}
			>
				<Text
					bold
					grow
					res="Status"
				/>
				{statusUser ? (
					<Box color="grey.A100" textAlign="right">
						{isResolved ? (
							<Fragment>
								<Text block res="CommentMarkedAsResolved" />
								<FlexRowCenter>
									<Text
										res="CommentResolvedBy"
										resParams={[statusUser, statusDate]}
										style={{ marginRight: 4 }}
										variant="body2"
									/>
									{canResolve && (
										<Link onClick={() => setSelectedCommentResolved(false)}>
											<Text res="Reopen" variant="body2" />
										</Link>
									)}
								</FlexRowCenter>
							</Fragment>
						)
							: (
								<Fragment>
									{canResolve && (
										<StyledLink onClick={() => setSelectedCommentResolved(true)}>
											<Text res="CommentMarkAsResolved" />
										</StyledLink>
									)}
									<Text
										block
										res="CommentReopenedBy"
										resParams={[statusUser, statusDate]}
										variant="body2"
									/>
								</Fragment>
							)}
					</Box>
				)
					: canResolve && (
						<Link onClick={() => setSelectedCommentResolved(true)}>
							<Text res="CommentMarkAsResolved" />
						</Link>
					)}
				<CommentStatusIcon
					design={canResolve ? null : "inactive"}
					isResolved={isResolved}
					onClick={() => setSelectedCommentResolved(!isResolved)}
				/>
			</FlexRowCenter>
			{isSelectedCommentStale &&
				<CommentOutdatedBanner onClick={fetchSelectedComment} />
			}
			<StyledBox>
				<CommentCard commentUrl={selectedCommentUrl} />
				{selectedCommentReplyUrls?.map((url) => (
					<CommentCard commentUrl={url} key={url} />
				))}
			</StyledBox>
			{isReply ? (
				<Fragment>
					<UserContext.Provider value={userProvider}>
						<FroalaEditorComponent
							configProps={{
								editorClass: "comment-reply",
								events: {
									blur: onBlur,
									"commands.after": onOpenPopups,
								},
								placeholderText: loc.resource("CommentsAddReply"),
							}}
							defaultValue={mentionUserText}
							heightMax={MAX_HEIGHT_OF_TOOLBAR}
							heightMin={MIN_HEIGHT_OF_TOOLBAR}
							moreActionProps={{
								buttons: [SEND_BUTTON, PRE_SEND_BUTTON],
								buttonsVisible: 2,
							}}
							onAttach={onAddAttachment}
							onAttachError={onAttachError}
							onModelChangeCallback={onContentChangeCallback}
							pullFiles={pullFiles}
							pushFilesPreOpenDialog={pushFilesPreOpenDialog}
							ref={ref}
							titleRes="CommentsAddReply"
							value=""
						/>
					</UserContext.Provider>
					{addedFiles.length > 0 ? (
						<Box
							flexShrink={0}
							maxHeight={150}
							overflow="hidden auto"
							px={3}
							py={2}
						>
							<Grid
								container
								direction="column"
								spacing={1}
								wrap="nowrap"
							>
								{addedFiles.map((fileData) => (
									<Grid item key={fileData.id}>
										<File
											fileData={fileData}
											onDelete={() => onDeleteAttachment(fileData)}
										/>
									</Grid>
								),
								)}
							</Grid>
						</Box>
					)
						:
						null
					}
				</Fragment>
			)
				: canReply && !isResolved && (
					<FlexRowCenter
						borderBottom={STANDARD_BORDER_STYLE}
						borderTop={STANDARD_BORDER_STYLE}
						color="grey.800"
						flexShrink={0}
						height={PLACEHOLDER_HEIGHT}
						onClick={startReply}
						px={4}
						py={4}
					>
						<Text res="CommentsAddReply" style={{ marginBottom: 1 }} />
					</FlexRowCenter>
				)}
		</>
	);
});

export default CommentDetail;